import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Select, Textarea, Button, IconButton, Spinner, Switch,
  Alert, AlertIcon, Collapse, Link, Tooltip, Divider, Tabs, TabList, Tab, TabPanels, TabPanel, useToast,
} from '@chakra-ui/react';
import {
  FiPlus, FiSend, FiFileText, FiChevronDown, FiChevronRight, FiExternalLink, FiInfo, FiFlag,
} from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import { StatusPill } from 'components/monitoring/ai/common';
import RequestDrawer from 'components/document-requests/RequestDrawer';
import {
  getInboxOptions, STATUS, PAYMENT, EDIT_ROLES, errorMessage, formatMoney,
} from 'api/administrador/documentRequests';
import {
  SIM_ROLES, CONVERSATION_STATUS, TOOL_LABEL, fileUrl, RATING, OPEN_FINDING_STATUSES,
  PROVIDER_LABEL, listSimulations, createSimulation, getSimulation, updateSimulation, sendSimulationMessage, simulatePayment, advanceSimulation,
  rateSimulation, reportFinding, listFindings, CHARGE_STATUS, PAYMENT_MODE_HINT, verifyPayment,
} from 'api/administrador/ventanillaSimulator';
import FindingModal from './FindingModal';
import FindingsTab from './FindingsTab';

/**
 * Simulador Ventanilla (fase S). El equipo escribe como si fuera un estudiante en WhatsApp y responde el
 * mismo agente que atenderá en HiBot. A la derecha, lo que hizo el agente: herramientas, resultados y
 * notas que solo ve el equipo (por ejemplo, el código de verificación, que en pruebas no se envía al correo).
 * El equipo califica cada conversación y reporta respuestas con la bandera; eso llega a la pestaña Hallazgos.
 * El cobro puede ser simulado o con Wompi sandbox (selector «Cobro»). Wompi devuelve a la
 * persona a esta página con ?id=<transacción>: se consulta el pago y la conversación sigue sola.
 */

const time = (d) => new Date(d).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

/** Texto descriptivo en un tooltip, para que la caja de prueba quede compacta. */
function Hint({ label }) {
  return (
    <Tooltip label={label} hasArrow placement="top" fontSize="12px" maxW="280px">
      <Box as="span" tabIndex={0} aria-label={label} display="inline-flex" color="gray.400" cursor="help" _focusVisible={{ outline: '2px solid #574feb', borderRadius: '4px' }}>
        <FiInfo size={13} />
      </Box>
    </Tooltip>
  );
}

/** Fila compacta de la caja de prueba: etiqueta (con su tooltip) a la izquierda y el valor a la derecha. */
function Row({ label, hint, htmlFor, children }) {
  return (
    <Flex justify="space-between" align="center" gap={3} minH="32px">
      <Flex align="center" gap={1.5} color="gray.600" fontSize="13px" flexShrink={0}>
        <Text as={htmlFor ? 'label' : 'span'} htmlFor={htmlFor}>{label}</Text>
        {hint && <Hint label={hint} />}
      </Flex>
      <Flex align="center" gap={2} justify="flex-end" minW={0} fontSize="13px">{children}</Flex>
    </Flex>
  );
}

/** *negrita* de WhatsApp y enlaces. */
function WhatsappText({ text }) {
  const lines = String(text || '').split('\n');
  return lines.map((line, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <Text key={i} fontSize="14px" minH={line ? undefined : '8px'} whiteSpace="pre-wrap" wordBreak="break-word">
      {line.split(/(\*[^*\n]+\*|https?:\/\/\S+)/g).map((part, j) => {
        // eslint-disable-next-line react/no-array-index-key
        if (/^\*[^*]+\*$/.test(part)) return <b key={j}>{part.slice(1, -1)}</b>;
        // eslint-disable-next-line react/no-array-index-key
        if (/^https?:\/\//.test(part)) return <Link key={j} color="#027eb5" href={part} isExternal>{part}</Link>;
        return part;
      })}
    </Text>
  ));
}

/** Bandera para reportar una respuesta; queda roja si ya tiene un reporte. */
function ReportButton({ onReport, reported }) {
  if (!onReport) return null;
  return (
    <Tooltip label={reported ? 'Ya tiene un reporte. Reportar otra cosa' : 'Reportar esta respuesta'} hasArrow fontSize="12px">
      <IconButton
        aria-label="Reportar esta respuesta" icon={<FiFlag />} size="xs" variant="ghost" minW="22px" h="22px"
        color={reported ? 'red.500' : 'gray.400'} _hover={{ color: reported ? 'red.600' : 'gray.600', bg: 'blackAlpha.50' }}
        onClick={onReport}
      />
    </Tooltip>
  );
}

function Bubble({ message, onReport, reported, focused }) {
  const focus = focused ? { outline: '2px solid #574feb', outlineOffset: '2px' } : {};
  if (message.role === 'aviso' || message.role === 'plantilla' || message.role === 'error') {
    const tone = {
      aviso: { bg: '#fff5c4', label: 'Aviso de la plataforma' },
      plantilla: { bg: '#e1f3fb', label: 'Mensaje automático (plantilla)' },
      error: { bg: '#fde2e1', label: 'Error' },
    }[message.role];
    return (
      <Flex justify="center" my={2} id={`sim-msg-${message.id}`}>
        <Box bg={tone.bg} borderRadius="8px" px={3} py={2} maxW="85%" boxShadow="0 1px 0.5px rgba(11,20,26,.13)" {...focus}>
          <Flex justify="space-between" align="center" gap={2} mb={1}>
            <Text fontSize="11px" fontWeight="700" color="gray.600">{tone.label}</Text>
            {message.role !== 'aviso' && <ReportButton onReport={onReport} reported={reported} />}
          </Flex>
          <WhatsappText text={message.text} />
          {message.role === 'error' && message.trace?.[0]?.detail && (
            <Text fontSize="11px" color="red.700" mt={1} fontFamily="mono">{message.trace[0].detail}</Text>
          )}
        </Box>
      </Flex>
    );
  }
  const mine = message.role === 'persona';
  return (
    <Flex justify={mine ? 'flex-end' : 'flex-start'} my={1} id={`sim-msg-${message.id}`}>
      <Box
        bg={mine ? '#d9fdd3' : 'white'} borderRadius="8px" px={3} pt={2} pb={1} maxW="78%"
        boxShadow="0 1px 0.5px rgba(11,20,26,.13)" opacity={message.pending ? 0.6 : 1} {...focus}
      >
        {message.text && <WhatsappText text={message.text} />}
        {(message.attachments || []).map((a) => (
          <Link key={a.url} href={fileUrl(a.url)} isExternal _hover={{ textDecoration: 'none' }}>
            <Flex mt={2} align="center" gap={3} bg="#f5f6f6" borderRadius="6px" px={3} py={2}>
              <Box as={FiFileText} color="#e5484d" boxSize="22px" />
              <Box minW={0}>
                <Text fontSize="13px" fontWeight="600" isTruncated>{a.name}</Text>
                <Text fontSize="11px" color="gray.500">PDF · código {a.code}</Text>
              </Box>
            </Flex>
          </Link>
        ))}
        <Flex justify="flex-end" align="center" gap={1} mt={0.5}>
          {!mine && <ReportButton onReport={onReport} reported={reported} />}
          <Text fontSize="10px" color="gray.500">{message.pending ? 'enviando…' : time(message.createdAt)}</Text>
        </Flex>
      </Box>
    </Flex>
  );
}

function TraceItem({ step }) {
  const [open, setOpen] = useState(false);
  if (step.type !== 'herramienta') {
    return (
      <Text fontSize="12px" color="orange.700" py={1}>
        {step.type === 'rechazo' ? 'El modelo rechazó responder' : step.type === 'limite' ? 'Límite de pasos' : step.type}
        {step.detail ? `: ${step.detail}` : ''}
      </Text>
    );
  }
  return (
    <Box borderTop="1px solid" borderColor="gray.100" py={2}>
      <Flex as="button" type="button" w="100%" align="center" gap={2} onClick={() => setOpen((o) => !o)} aria-expanded={open} textAlign="left">
        <Box as={open ? FiChevronDown : FiChevronRight} color="gray.400" flexShrink={0} />
        <Text fontSize="13px" fontWeight="600" flex="1">{TOOL_LABEL[step.name] || step.name}</Text>
        <StatusPill label={step.ok ? 'OK' : 'No'} tone={step.ok ? 'green' : 'red'} size="10px" />
        <Text fontSize="11px" color="gray.400" fontFamily="mono">{step.ms} ms</Text>
      </Flex>
      {step.panel && (
        <Text fontSize="12px" mt={1} ml={6} bg="#fff5c4" borderRadius="6px" px={2} py={1}>{step.panel}</Text>
      )}
      {!step.ok && step.result?.error && <Text fontSize="12px" color="red.600" mt={1} ml={6}>{step.result.error}</Text>}
      <Collapse in={open} animateOpacity>
        <Box ml={6} mt={2}>
          <Text fontSize="11px" fontWeight="700" color="gray.500">Entrada</Text>
          <Box as="pre" fontSize="11px" bg="gray.50" borderRadius="6px" p={2} whiteSpace="pre-wrap" wordBreak="break-word">{JSON.stringify(step.input, null, 2)}</Box>
          <Text fontSize="11px" fontWeight="700" color="gray.500" mt={2}>Resultado (lo que lee el agente)</Text>
          <Box as="pre" fontSize="11px" bg="gray.50" borderRadius="6px" p={2} whiteSpace="pre-wrap" wordBreak="break-word">{JSON.stringify(step.result, null, 2)}</Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function TracePanel({ messages }) {
  // Cada respuesta del agente, con el mensaje que la disparó; la más reciente arriba.
  const turns = [];
  messages.forEach((m, i) => {
    if (m.role !== 'agente') return;
    const trigger = [...messages.slice(0, i)].reverse().find((x) => x.role === 'persona' || x.role === 'aviso');
    turns.push({ m, trigger });
  });
  if (!turns.length) return <Text fontSize="13px" color="gray.500">Aquí aparece lo que hace el agente en cada respuesta.</Text>;
  return turns.reverse().map(({ m, trigger }) => (
    <Box key={m.id} mb={4}>
      <Text fontSize="12px" color="gray.500" isTruncated>
        {time(m.createdAt)} · {trigger ? `«${trigger.text}»` : ''}
      </Text>
      {(m.trace || []).length === 0 && <Text fontSize="12px" color="gray.400" py={1}>Respondió sin usar herramientas.</Text>}
      {(m.trace || []).map((step, i) => <TraceItem key={`${m.id}-${i}`} step={step} />)}
      {m.usage && (
        <Text fontSize="11px" color="gray.400" mt={1} fontFamily="mono">
          {`tokens: entrada ${(m.usage.input_tokens || 0) + (m.usage.cache_read_input_tokens || 0) + (m.usage.cache_creation_input_tokens || 0)} (caché ${m.usage.cache_read_input_tokens || 0}) · salida ${m.usage.output_tokens || 0}`}
        </Text>
      )}
    </Box>
  ));
}

export default function VentanillaSimulador() {
  const { user } = useAuth();
  const role = user?.role?.type;
  const isAllowed = SIM_ROLES.includes(role);
  const toast = useToast();

  const [list, setList] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [options, setOptions] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [phoneMatches, setPhoneMatches] = useState(true);
  const [providers, setProviders] = useState(null);
  const [provider, setProvider] = useState('');
  const [openRequest, setOpenRequest] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [scenario, setScenario] = useState('libre');
  const [tab, setTab] = useState(0);
  const [counts, setCounts] = useState({});
  const [findingsKey, setFindingsKey] = useState(0);
  const [reporting, setReporting] = useState(null);
  const [focusMessage, setFocusMessage] = useState(null);
  const [ratingDraft, setRatingDraft] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [payment, setPayment] = useState('simulado');
  const bottom = useRef(null);
  const returnHandled = useRef(false);

  const refreshList = useCallback(() => listSimulations().then((r) => {
    setList(r.items);
    setProviders(r.providers);
    setScenarios(r.scenarios);
    setPaymentModes(r.paymentModes);
    if (r.providers) setProvider((current) => current || r.providers.default);
  }).catch(() => {}), []);

  const refreshCounts = useCallback(() => listFindings({ status: 'abiertos' }).then((r) => setCounts(r.meta.counts)).catch(() => {}), []);

  useEffect(() => {
    if (!isAllowed) return;
    refreshList();
    refreshCounts();
    getInboxOptions().then(setOptions).catch(() => setOptions(null));
  }, [isAllowed, refreshList, refreshCounts]);

  // Regreso desde Wompi: se consulta la transacción y se abre su conversación.
  useEffect(() => {
    if (!isAllowed || returnHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const transactionId = params.get('id');
    if (!transactionId) return;
    returnHandled.current = true;
    params.delete('id');
    params.delete('env');
    const rest = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
    setBusy('verificar');
    verifyPayment(transactionId)
      .then((r) => {
        if (r.conversation) {
          setConversation(r.conversation);
          setPayment(r.conversation.paymentMode || 'simulado');
          setScenario(r.conversation.scenario || 'libre');
          setPhoneMatches(!!r.conversation.settings?.phoneMatches);
        }
        const label = CHARGE_STATUS[r.charge?.status]?.label || 'consultado';
        toast({ status: r.charge?.status === 'aprobado' ? 'success' : 'info', title: `Pago en Wompi: ${label.toLowerCase()}.`, duration: 5000, isClosable: true });
        refreshList();
      })
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(''));
  }, [isAllowed, toast, refreshList]);

  useEffect(() => {
    if (focusMessage) return;
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversation?.messages?.length, sending, focusMessage]);

  // Al abrir un hallazgo se va al mensaje reportado.
  useEffect(() => {
    if (!focusMessage || tab !== 0) return undefined;
    const t = setTimeout(() => document.getElementById(`sim-msg-${focusMessage}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    return () => clearTimeout(t);
  }, [focusMessage, tab, conversation?.id]);

  const open = async (id, messageId) => {
    setRatingDraft(null);
    setFocusMessage(messageId || null);
    if (!id) { setConversation(null); return; }
    setError('');
    try {
      const c = await getSimulation(id);
      setConversation(c);
      setPhoneMatches(!!c.settings?.phoneMatches);
      setScenario(c.scenario || 'libre');
      setPayment(c.paymentMode || 'simulado');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const openFinding = (id, messageId) => {
    setTab(0);
    open(id, messageId);
  };

  const start = async () => {
    setBusy('nueva');
    setError('');
    setRatingDraft(null);
    setFocusMessage(null);
    try {
      const c = await createSimulation({ phoneMatches, provider: provider || undefined, scenario, payment });
      setConversation(c);
      refreshList();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  };

  const send = async () => {
    const value = text.trim();
    if (!value || !conversation || sending) return;
    setSending(true);
    setError('');
    setText('');
    setFocusMessage(null);
    // Se muestra de inmediato; la respuesta del agente trae la conversación completa.
    setConversation((c) => ({ ...c, messages: [...c.messages, { id: `p-${Date.now()}`, role: 'persona', text: value, pending: true, createdAt: new Date().toISOString() }] }));
    try {
      setConversation(await sendSimulationMessage(conversation.id, value));
      refreshList();
    } catch (e) {
      setError(errorMessage(e));
      open(conversation.id);
    } finally {
      setSending(false);
    }
  };

  const action = async (key, fn, done) => {
    setBusy(key);
    setError('');
    try {
      const c = await fn();
      setConversation(c);
      if (done) toast({ status: 'info', title: done(c), duration: 4000, isClosable: true });
      refreshList();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  };

  const togglePhone = async (value) => {
    setPhoneMatches(value);
    if (conversation) action('celular', () => updateSimulation(conversation.id, { phoneMatches: value }));
  };

  const changeScenario = (value) => {
    setScenario(value);
    if (conversation) action('escenario', () => updateSimulation(conversation.id, { scenario: value }));
  };

  // Con conversación abierta se cambia en ella (la API no deja si ya hay un cobro); si falla, vuelve a su modo.
  const changePayment = async (value) => {
    if (!conversation) { setPayment(value); return; }
    setBusy('cobro');
    setError('');
    try {
      const c = await updateSimulation(conversation.id, { payment: value });
      setConversation(c);
      setPayment(c.paymentMode || 'simulado');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  };

  const findingsChanged = () => {
    refreshCounts();
    setFindingsKey((k) => k + 1);
  };

  const rate = (value) => {
    if (value === 'correcta') {
      setRatingDraft(null);
      action('calificacion', () => rateSimulation(conversation.id, { rating: value }), () => 'Conversación calificada.');
      return;
    }
    setRatingDraft({ value, comment: conversation.rating?.value === value ? conversation.rating.comment || '' : '' });
  };

  const saveRating = () => action(
    'calificacion',
    () => rateSimulation(conversation.id, { rating: ratingDraft.value, comment: ratingDraft.comment }),
    () => { setRatingDraft(null); findingsChanged(); return 'Calificación guardada. Quedó como hallazgo para revisarla.'; },
  );

  const submitReport = async (body) => {
    try {
      const c = await reportFinding(conversation.id, { ...body, messageId: reporting.id });
      setConversation(c);
      setReporting(null);
      findingsChanged();
      toast({ status: 'success', title: 'Reporte enviado. Lo ves en la pestaña Hallazgos.', duration: 4000, isClosable: true });
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e), duration: 5000, isClosable: true });
    }
  };

  const reported = new Set((conversation?.findings || []).filter((f) => f.messageId).map((f) => f.messageId));
  const openCount = OPEN_FINDING_STATUSES.reduce((n, s) => n + (counts[s] || 0), 0);
  const currentModel = conversation && (PROVIDER_LABEL[conversation.provider] || conversation.provider);

  if (!isAllowed) {
    return (
      <Box p={8}>
        <Text color="gray.500">Esta vista está disponible solo para el área administrativa.</Text>
      </Box>
    );
  }

  const request = conversation?.request;
  const st = conversation ? CONVERSATION_STATUS[conversation.status] : null;
  const closedForAgent = conversation?.status === 'transferida';

  return (
    <Box p={{ base: 3, md: 6 }}>
      <Flex mb={5} justify="space-between" align="flex-start" gap={4} wrap="wrap">
        <Flex align="center" gap={2}>
          <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em">Simulador Ventanilla</Heading>
          <Hint label="Escribe como si fueras un estudiante en WhatsApp. Responde el mismo agente que atenderá en HiBot, con las mismas reglas. Todo queda como prueba: no se envían correos, el cobro es simulado y el PDF sale con la marca «Documento de prueba»." />
        </Flex>
        <Flex gap={3} align="center" wrap="wrap">
          <Select
            aria-label="Conversación" w={{ base: '100%', sm: '260px' }} borderRadius="8px" bg="white"
            value={conversation?.id || ''} onChange={(e) => open(e.target.value)}
          >
            <option value="">Conversaciones anteriores…</option>
            {list.map((c) => (
              <option key={c.id} value={c.id}>
                {`#${c.id} · ${new Date(c.createdAt).toLocaleDateString('es-CO')} · ${c.createdBy || ''}${c.request ? ` · ${c.request.number}` : ''}${c.rating ? ` · ${RATING[c.rating]?.label}` : ''}`}
              </option>
            ))}
          </Select>
          <Button leftIcon={<FiPlus />} bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} borderRadius="10px" onClick={start} isLoading={busy === 'nueva'}>
            Nueva conversación
          </Button>
        </Flex>
      </Flex>

      {error && <Alert status="error" borderRadius="10px" mb={4} fontSize="sm"><AlertIcon />{error}</Alert>}
      {busy === 'verificar' && <Alert status="info" borderRadius="10px" mb={4} fontSize="sm"><Spinner size="sm" mr={3} />Consultando el pago en Wompi…</Alert>}

      <Tabs index={tab} onChange={setTab} colorScheme="purple" isLazy>
        <TabList mb={4}>
          <Tab fontSize="14px">Conversación</Tab>
          <Tab fontSize="14px" gap={2}>
            Hallazgos
            {openCount > 0 && <StatusPill label={String(openCount)} tone="purple" size="10px" />}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <Grid templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) 380px' }} gap={5} alignItems="start">
              {/* Chat */}
              <Box border="1px solid" borderColor="gray.200" borderRadius="14px" overflow="hidden" bg="#efeae2">
                <Flex bg="#075e54" color="white" px={4} py={3} align="center" justify="space-between">
                  <Box>
                    <Text fontWeight="700" fontSize="15px">Klazia · Ventanilla</Text>
                    <Text fontSize="12px" opacity={0.85}>{conversation ? `Conversación de prueba #${conversation.id}` : 'Sin conversación'}</Text>
                  </Box>
                  {st && <StatusPill label={st.label} tone={st.tone} />}
                </Flex>
                <Box h={{ base: '460px', md: '560px' }} overflowY="auto" px={{ base: 3, md: 6 }} py={4}>
                  {!conversation && (
                    <Flex h="100%" align="center" justify="center" direction="column" gap={3}>
                      <Text color="gray.600" fontSize="14px" textAlign="center" maxW="360px">
                        Empieza una conversación nueva. Puedes escribir, por ejemplo: «Hola, necesito una constancia de estudio».
                      </Text>
                      <Button size="sm" variant="outline" bg="white" onClick={start} isLoading={busy === 'nueva'}>Nueva conversación</Button>
                    </Flex>
                  )}
                  {conversation?.messages.map((m) => (
                    <Bubble
                      key={m.id} message={m} reported={reported.has(m.id)} focused={focusMessage === m.id}
                      onReport={['agente', 'plantilla', 'error'].includes(m.role) && !m.pending ? () => setReporting(m) : null}
                    />
                  ))}
                  {sending && (
                    <Flex my={1}><Box bg="white" borderRadius="8px" px={3} py={2}><Text fontSize="13px" color="gray.500">escribiendo…</Text></Box></Flex>
                  )}
                  {closedForAgent && (
                    <Text textAlign="center" fontSize="12px" color="gray.600" my={3}>
                      La conversación pasó a una persona: en HiBot la continuaría el equipo. El agente ya no responde.
                    </Text>
                  )}
                  <div ref={bottom} />
                </Box>
                <Flex bg="#f0f2f5" px={3} py={2} gap={2} align="flex-end">
                  <Textarea
                    aria-label="Mensaje" placeholder={conversation ? 'Escribe un mensaje' : 'Crea una conversación para escribir'}
                    value={text} onChange={(e) => setText(e.target.value)} rows={1} resize="none" bg="white" borderRadius="20px"
                    isDisabled={!conversation || sending}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  />
                  <IconButton
                    aria-label="Enviar" icon={<FiSend />} borderRadius="full" bg="#00a884" color="white" _hover={{ bg: '#008f72' }}
                    onClick={send} isLoading={sending} isDisabled={!conversation || !text.trim()}
                  />
                </Flex>
              </Box>

              {/* Panel del equipo */}
              <Box>
                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" px={4} py={3} mb={4}>
                  <Flex justify="space-between" align="center" mb={1} gap={2}>
                    <Text fontWeight="700" fontSize="14px" isTruncated>
                      {conversation ? `Prueba #${conversation.id}` : 'Prueba'}
                      {conversation && <Text as="span" fontWeight="400" color="gray.500">{` · ${currentModel}`}</Text>}
                    </Text>
                    <Hint label="Todo queda como prueba: no se envían correos y el PDF sale con la marca «Documento de prueba». El cobro es el que elijas en «Cobro». Para reportar una respuesta usa la bandera del mensaje." />
                  </Flex>
                  <Row label="Escenario" hint="El caso que vas a probar. Sirve para agrupar las calificaciones y los hallazgos." htmlFor="sim-scenario">
                    <Select
                      id="sim-scenario" size="xs" w="210px" borderRadius="6px" value={scenario}
                      onChange={(e) => changeScenario(e.target.value)} isDisabled={busy === 'escenario'}
                    >
                      {(scenarios.length ? scenarios : [{ value: 'libre', label: 'Prueba libre' }]).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Select>
                  </Row>
                  <Row
                    label="Celular registrado" htmlFor="sim-phone"
                    hint="Encendido: el estudiante escribe desde su celular registrado y queda verificado. Apagado: el agente pide un código que en WhatsApp llegaría al correo; aquí no se envía, aparece en «Lo que hizo el agente»."
                  >
                    <Switch id="sim-phone" size="sm" colorScheme="purple" isChecked={phoneMatches} onChange={(e) => togglePhone(e.target.checked)} isDisabled={busy === 'celular'} />
                  </Row>
                  {providers && (
                    <Row
                      label="Modelo" htmlFor="sim-provider"
                      hint={`${conversation ? `Modelo para la próxima conversación nueva. Esta usa ${currentModel}.` : 'Modelo para la conversación nueva. Cada conversación queda con el suyo.'} ${providers.options.map((o) => `${PROVIDER_LABEL[o.value] || o.value}: ${o.model}`).join(' · ')}.`}
                    >
                      <Select id="sim-provider" size="xs" w="210px" borderRadius="6px" value={provider} onChange={(e) => setProvider(e.target.value)}>
                        {providers.options.map((o) => (
                          <option key={o.value} value={o.value}>{PROVIDER_LABEL[o.value] || o.value}</option>
                        ))}
                      </Select>
                    </Row>
                  )}

                  {paymentModes.length > 0 && (
                    <Row
                      label="Cobro" htmlFor="sim-payment"
                      hint={paymentModes.map((m) => `${m.label}: ${m.available ? PAYMENT_MODE_HINT[m.value] || '' : `no disponible (${m.reason})`}`).join(' · ')}
                    >
                      <Select
                        id="sim-payment" size="xs" w="210px" borderRadius="6px" value={payment}
                        onChange={(e) => changePayment(e.target.value)}
                        isDisabled={busy === 'cobro' || !!conversation?.charge || ['esperando_pago'].includes(request?.status) || request?.paymentStatus === 'pagado'}
                      >
                        {paymentModes.map((m) => (
                          <option key={m.value} value={m.value} disabled={!m.available}>
                            {m.available ? m.label : `${m.label} (no disponible)`}
                          </option>
                        ))}
                      </Select>
                    </Row>
                  )}

                  {conversation && (
                    <>
                      <Divider my={2} />
                      <Row label="Identidad">
                        <Text fontWeight="600">
                          {conversation.identityVerified ? `Verificada (${conversation.identityMethod === 'celular' ? 'celular' : 'código'})` : 'Sin verificar'}
                        </Text>
                      </Row>
                      <Row label="Estudiante">
                        <Text fontWeight="600" fontFamily="mono">{conversation.studentId ? `Código ${conversation.studentId}` : '—'}</Text>
                      </Row>
                      <Row label="Solicitud">
                        {request ? (
                          <Button size="xs" variant="link" color="#574feb" rightIcon={<FiExternalLink />} onClick={() => setOpenRequest(request.id)}>
                            {request.number}
                          </Button>
                        ) : <Text>—</Text>}
                      </Row>
                      {request && (
                        <Row label="Estado">
                          {STATUS[request.status] && <StatusPill label={STATUS[request.status].label} tone={STATUS[request.status].tone} size="10px" />}
                          {PAYMENT[request.paymentStatus] && <StatusPill label={`Pago ${PAYMENT[request.paymentStatus].label.toLowerCase()}`} tone={PAYMENT[request.paymentStatus].tone} size="10px" />}
                          <Text fontSize="12px" color="gray.600">{formatMoney(request.amount)}</Text>
                        </Row>
                      )}
                      {conversation.charge && (
                        <Row label={`Cobro ${conversation.charge.modeLabel}`}>
                          {CHARGE_STATUS[conversation.charge.status] && (
                            <StatusPill label={CHARGE_STATUS[conversation.charge.status].label} tone={CHARGE_STATUS[conversation.charge.status].tone} size="10px" />
                          )}
                          <Tooltip label={`Referencia ${conversation.charge.reference}${conversation.charge.transactionId ? ` · transacción ${conversation.charge.transactionId}` : ''}`} hasArrow fontSize="12px">
                            <Text fontSize="12px" color="gray.600" tabIndex={0}>{formatMoney(conversation.charge.amount)}</Text>
                          </Tooltip>
                        </Row>
                      )}
                      {request?.status === 'esperando_pago' && (
                        <Flex gap={2} mt={2}>
                          {conversation.paymentMode === 'simulado' ? (
                            <Button
                              flex="1" size="xs" bg="#00a884" color="white" _hover={{ bg: '#008f72' }} isLoading={busy === 'pago'}
                              onClick={() => action('pago', () => simulatePayment(conversation.id))}
                            >
                              Simular pago
                            </Button>
                          ) : (
                            <>
                              <Button
                                as={Link} href={conversation.charge?.url || undefined} isExternal flex="1" size="xs" bg="#00a884" color="white"
                                _hover={{ bg: '#008f72', textDecoration: 'none' }} rightIcon={<FiExternalLink />} isDisabled={!conversation.charge?.url}
                              >
                                Pagar en Wompi
                              </Button>
                              <Tooltip label="Vuelve a consultar la conversación por si ya llegó el aviso de Wompi." hasArrow fontSize="12px">
                                <Button size="xs" variant="outline" isLoading={busy === 'actualizar'} onClick={() => action('actualizar', () => getSimulation(conversation.id))}>
                                  Actualizar
                                </Button>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip label="Adelanta 24 horas: envía el recordatorio de pago." hasArrow fontSize="12px">
                            <Button
                              size="xs" variant="outline" isLoading={busy === '24'}
                              onClick={() => action('24', () => advanceSimulation(conversation.id, 24), (c) => (c.sweep ? 'Se envió el recordatorio.' : 'Nada que hacer: el recordatorio ya se envió.'))}
                            >
                              +24 h
                            </Button>
                          </Tooltip>
                          <Tooltip label="Adelanta 3 días: la solicitud queda abandonada." hasArrow fontSize="12px">
                            <Button
                              size="xs" variant="outline" isLoading={busy === '72'}
                              onClick={() => action('72', () => advanceSimulation(conversation.id, 72), () => 'La solicitud quedó abandonada.')}
                            >
                              +3 días
                            </Button>
                          </Tooltip>
                        </Flex>
                      )}

                      <Divider my={2} />
                      <Row label="¿Resolvió bien?" hint="Califica la conversación al terminar. Parcial o No queda como hallazgo para revisarlo.">
                        {Object.entries(RATING).map(([key, r]) => {
                          const active = (ratingDraft?.value || conversation.rating?.value) === key;
                          return (
                            <Button
                              key={key} size="xs" borderRadius="6px" fontWeight="500" aria-pressed={active}
                              variant={active ? 'solid' : 'outline'} bg={active ? '#574feb' : 'white'} color={active ? 'white' : 'gray.700'}
                              _hover={{ bg: active ? '#4a42d6' : 'gray.50' }} isLoading={busy === 'calificacion' && active}
                              onClick={() => rate(key)}
                            >
                              {r.label}
                            </Button>
                          );
                        })}
                      </Row>
                      {ratingDraft && (
                        <Box mt={2}>
                          <Textarea
                            aria-label="Qué faltó o qué salió mal" rows={2} fontSize="13px" borderRadius="8px" autoFocus
                            placeholder="¿Qué faltó o qué salió mal?"
                            value={ratingDraft.comment} onChange={(e) => setRatingDraft((d) => ({ ...d, comment: e.target.value }))}
                          />
                          <Flex justify="flex-end" gap={2} mt={2}>
                            <Button size="xs" variant="ghost" onClick={() => setRatingDraft(null)}>Cancelar</Button>
                            <Button size="xs" bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={saveRating} isLoading={busy === 'calificacion'}>
                              Guardar
                            </Button>
                          </Flex>
                        </Box>
                      )}
                      {conversation.rating && !ratingDraft && (
                        <Text fontSize="11px" color="gray.500" textAlign="right">
                          {`Calificada por ${conversation.rating.by}${conversation.rating.comment ? ` · «${conversation.rating.comment}»` : ''}`}
                        </Text>
                      )}
                    </>
                  )}
                </Box>

                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4} maxH="560px" overflowY="auto">
                  <Text fontWeight="700" fontSize="14px" mb={3}>Lo que hizo el agente</Text>
                  {conversation ? <TracePanel messages={conversation.messages} /> : <Text fontSize="13px" color="gray.500">Sin conversación.</Text>}
                  {sending && <Flex align="center" gap={2} mt={2}><Spinner size="xs" color="#574feb" /><Text fontSize="12px" color="gray.500">Pensando…</Text></Flex>}
                </Box>
              </Box>
            </Grid>
          </TabPanel>
          <TabPanel p={0}>
            <FindingsTab scenarios={scenarios} onOpen={openFinding} onCounts={setCounts} refreshKey={findingsKey} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <FindingModal isOpen={!!reporting} onClose={() => setReporting(null)} onSubmit={submitReport} message={reporting} />

      <RequestDrawer
        requestId={openRequest}
        isOpen={!!openRequest}
        onClose={() => setOpenRequest(null)}
        options={options}
        canEdit={EDIT_ROLES.includes(role)}
        onChanged={() => conversation && open(conversation.id)}
      />
    </Box>
  );
}
